/**
 * HangarMax import — queries and mutations.
 *
 * The planning mutation is deliberately separate from the executing one: the
 * user reviews a full `ImportPlan` (including everything that will NOT be
 * imported) before a single PlaneMAX row is written.
 */

import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useTenant } from '@/contexts'
import { aircraftKeys } from './useAircraft'
import { allocationKeys } from './useAllocations'
import { hangarKeys } from './useHangars'
import { obstacleKeys } from './useObstacles'
import { unitKeys } from './useUnits'
import {
  deleteImportSource,
  fetchAircraftTypeCandidates,
  fetchCatalogEntries,
  fetchExistingTargetIds,
  fetchImportLinks,
  fetchImportSource,
  fetchImportSources,
  findImportSource,
  fetchSharedLayout,
  finalizeImportSource,
  upsertImportSource,
} from '@/lib/api/hangarmaxImport'
import {
  buildImportPlan,
  executeImportPlan,
  hashObject,
  itemCacheCatalogSource,
  manifestCatalogSource,
  mergedCatalogSource,
  sourceMetaFor,
  type ExecuteProgress,
  type ExecuteResult,
  type HmManifestItem,
  type ImportPlan,
  type ParsedBundle,
  type ParsedLayoutOption,
} from '@/lib/hangarmax-import'

export const hangarmaxImportKeys = {
  all: ['hangarmax-import'] as const,
  sources: (locationId: string) => [...hangarmaxImportKeys.all, 'sources', locationId] as const,
  links: (sourceId: string) => [...hangarmaxImportKeys.all, 'links', sourceId] as const,
}

export function useHangarmaxImportSources(locationId: string | undefined) {
  return useQuery({
    queryKey: hangarmaxImportKeys.sources(locationId ?? ''),
    queryFn: () => fetchImportSources(locationId!),
    enabled: Boolean(locationId),
  })
}

export function useHangarmaxImportLinks(sourceId: string | undefined) {
  return useQuery({
    queryKey: hangarmaxImportKeys.links(sourceId ?? ''),
    queryFn: () => fetchImportLinks(sourceId!),
    enabled: Boolean(sourceId),
  })
}

/** Everything the review step needs: the plan plus what produced it. */
export interface PreparedImport {
  plan: ImportPlan
  bundle: ParsedBundle
  layout: ParsedLayoutOption
  catalogEntries: HmManifestItem[]
  existingSourceId: string | null
}

interface PrepareParams {
  locationId: string
  locationCenter: { lat: number; lng: number }
  bundle: ParsedBundle
  layout: ParsedLayoutOption
  sourceKind: 'bundle_upload' | 'share_link'
  shareToken?: string | null
  /** Manifest entries already fetched (share flow); otherwise resolved here. */
  catalogEntries?: HmManifestItem[]
}

/**
 * Build the import plan. Reads only — nothing is written until
 * {@link useExecuteHangarmaxImport} runs.
 */
export function usePrepareHangarmaxImport() {
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async (params: PrepareParams): Promise<PreparedImport> => {
      const organizationId = currentOrganization?.id
      if (!organizationId) throw new Error('No organization selected')

      const { layout, bundle } = params
      const sceneHash = hashObject(layout.sceneData)

      const meta = sourceMetaFor(bundle, layout, {
        sourceKind: params.sourceKind,
        shareToken: params.shareToken ?? null,
        fallbackToken: `upload:${sceneHash}`,
      })

      // Manifest entries carry the display names that make aircraft-type
      // matching work; item_cache alone only supports matching by size.
      let catalogEntries = params.catalogEntries ?? []
      if (catalogEntries.length === 0) {
        const ids = (layout.sceneData.items ?? [])
          .map((item) => item.catalog_id)
          .filter((id): id is string => typeof id === 'string')
        catalogEntries = await fetchCatalogEntries(ids)
      }

      const catalog = mergedCatalogSource(
        manifestCatalogSource(catalogEntries),
        itemCacheCatalogSource(layout.itemCache)
      )

      const [aircraftTypes, existingSource] = await Promise.all([
        fetchAircraftTypeCandidates(),
        findImportSource(params.locationId, {
          sourceLayoutId: meta.sourceLayoutId,
          shareToken: meta.shareToken,
        }),
      ])

      const existingLinks = existingSource ? await fetchImportLinks(existingSource.id) : []
      const existingTargetIds =
        existingLinks.length > 0 ? await fetchExistingTargetIds(existingLinks) : new Set<string>()

      const plan = buildImportPlan({
        sceneData: layout.sceneData,
        catalog,
        meta,
        ctx: {
          organizationId,
          locationId: params.locationId,
          locationCenter: params.locationCenter,
          aircraftTypes,
          existingLinks,
          previousSceneHash: existingSource?.sceneHash ?? null,
          existingTargetIds,
          now: new Date(),
        },
      })

      return {
        plan,
        bundle,
        layout,
        catalogEntries,
        existingSourceId: existingSource?.id ?? null,
      }
    },
    onError: (error: Error) => {
      toast.error('Could not prepare the import', { description: error.message })
    },
  })
}

interface ExecuteParams {
  locationId: string
  prepared: PreparedImport
  onProgress?: (progress: ExecuteProgress) => void
}

export function useExecuteHangarmaxImport() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async (params: ExecuteParams): Promise<ExecuteResult> => {
      const organizationId = currentOrganization?.id
      if (!organizationId) throw new Error('No organization selected')

      const { prepared, locationId } = params
      const { plan, layout, bundle, catalogEntries } = prepared

      const sourceId = await upsertImportSource({
        id: prepared.existingSourceId,
        organizationId,
        locationId,
        meta: plan.meta,
        sceneData: layout.sceneData,
        itemCache: layout.itemCache,
        rawEnvelope: bundle.rawEnvelope,
        catalogManifestSnapshot: catalogEntries.length > 0 ? catalogEntries : null,
        sceneHash: plan.sceneHash,
        anchor: layout.sceneData.anchor,
      })

      const result = await executeImportPlan({
        organizationId,
        locationId,
        sourceId,
        plan,
        onProgress: params.onProgress,
      })

      await finalizeImportSource(sourceId, result.counts)
      return result
    },
    onSuccess: (result, params) => {
      queryClient.invalidateQueries({ queryKey: hangarKeys.all })
      queryClient.invalidateQueries({ queryKey: unitKeys.all })
      queryClient.invalidateQueries({ queryKey: aircraftKeys.all })
      queryClient.invalidateQueries({ queryKey: allocationKeys.all })
      queryClient.invalidateQueries({ queryKey: obstacleKeys.all })
      queryClient.invalidateQueries({ queryKey: ['map-allocations'] })
      queryClient.invalidateQueries({
        queryKey: hangarmaxImportKeys.sources(params.locationId),
      })

      const { created, updated, unmatched } = result.counts
      if (result.errors.length > 0) {
        toast.warning(
          `Imported with ${result.errors.length} error${result.errors.length === 1 ? '' : 's'}`,
          { description: `${created} created, ${updated} updated, ${unmatched} not imported.` }
        )
      } else {
        toast.success('HangarMax layout imported', {
          description: `${created} created, ${updated} updated, ${unmatched} not imported.`,
        })
      }
    },
    onError: (error: Error) => {
      toast.error('Import failed', { description: error.message })
    },
  })
}

/** Re-plan an existing source from its preserved scene, then execute. */
export function useResyncHangarmaxImport() {
  const prepare = usePrepareHangarmaxImport()

  return useMutation({
    mutationFn: async (params: {
      sourceId: string
      locationId: string
      locationCenter: { lat: number; lng: number }
    }): Promise<PreparedImport> => {
      const source = await fetchImportSource(params.sourceId)

      // A share link is re-fetched so the resync sees the latest published
      // scene; an uploaded bundle can only be replayed from what we stored.
      if (source.sourceKind === 'share_link' && source.shareToken) {
        const fresh = await fetchSharedLayout(source.shareToken)
        return prepare.mutateAsync({
          locationId: params.locationId,
          locationCenter: params.locationCenter,
          sourceKind: 'share_link',
          shareToken: fresh.shareToken,
          catalogEntries: fresh.catalogEntries,
          bundle: {
            kind: 'share_scene',
            layouts: [],
            projectRef: null,
            exportVersion: null,
            rawEnvelope: null,
            sourceLocation: null,
          },
          layout: {
            layoutId: source.sourceLayoutId,
            name: fresh.layoutName ?? source.layoutName ?? 'Shared layout',
            deleted: false,
            sceneData: fresh.sceneData,
            itemCache: null,
            buildingCount: fresh.sceneData.buildings?.length ?? 0,
            itemCount: fresh.sceneData.items?.length ?? 0,
          },
        })
      }

      if (!source.sceneData) {
        throw new Error('This import has no stored scene to re-sync from.')
      }

      return prepare.mutateAsync({
        locationId: params.locationId,
        locationCenter: params.locationCenter,
        sourceKind: source.sourceKind,
        shareToken: source.shareToken,
        bundle: {
          kind: 'layout',
          layouts: [],
          projectRef: null,
          exportVersion: null,
          rawEnvelope: null,
          sourceLocation: null,
        },
        layout: {
          layoutId: source.sourceLayoutId,
          name: source.layoutName ?? 'Imported layout',
          deleted: false,
          sceneData: source.sceneData,
          itemCache: source.itemCache,
          buildingCount: source.sceneData.buildings?.length ?? 0,
          itemCount: source.sceneData.items?.length ?? 0,
        },
      })
    },
    onError: (error: Error) => {
      toast.error('Re-sync failed', { description: error.message })
    },
  })
}

export function useDeleteHangarmaxImportSource() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string; locationId: string }) => deleteImportSource(id),
    onSuccess: (_data, { locationId }) => {
      queryClient.invalidateQueries({
        queryKey: hangarmaxImportKeys.sources(locationId),
      })
      toast.success('Import link removed', {
        description: 'Imported buildings and aircraft were left in place.',
      })
    },
    onError: (error: Error) => {
      toast.error('Could not remove the import link', { description: error.message })
    },
  })
}

/** Share-link fetch, kept separate so the dialog can show its own state. */
export function useFetchSharedLayout() {
  return useMutation({
    mutationFn: (urlOrToken: string) => fetchSharedLayout(urlOrToken),
    onError: (error: Error) => {
      toast.error('Could not load the share link', { description: error.message })
    },
  })
}

/** Progress state for the execution step. */
export function useImportProgress() {
  const [progress, setProgress] = useState<ExecuteProgress | null>(null)
  const reset = useCallback(() => setProgress(null), [])
  return { progress, setProgress, reset }
}
